// Official fillable tax form (federal TD1 or the provincial TD1 for the staff member's province):
// the staff member types directly into the PDF. On submit, the typed field values + drawn signature
// are sent to the backend, which fills the same stored PDF, stamps the signature, flattens it and
// saves it to S3. The form's layout (amount lines, total, date field) comes from the backend.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../api";
import { SignaturePanel } from "../../shared/SignaturePanel";
import { showSuccess, showError } from "../../utils/dialog";
import talentLogo from "../../assets/talentlogo2.png";
import "../../Styles/pdfjs-viewer.css";

// pdf_viewer.js reads the core library from globalThis.pdfjsLib (window), so load it first (also keeps
// pdf.js out of the main bundle: it only loads on these pages)
async function loadPdfJs() {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdfjs/pdf.worker.min.js`;
  window.pdfjsLib = pdfjsLib; // = globalThis in the browser
  const viewer = await import("pdfjs-dist/web/pdf_viewer");
  return { pdfjsLib, viewer };
}

// pdf.js PDFPageView multiplies its `scale` by this (PDF points -> CSS px) for the page size and
// --scale-factor, so the scale we pass is divided by it to make the page exactly the area's width
const PDF_TO_CSS_UNITS = 96 / 72;

const parseAmount = (v) => {
  const n = Number(String(v ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {string} itemId       registration item ("td1-tax" | "tdon-tax")
 * @param {string} metaPath     GET -> { title, amountFields, totalField, autoTotal, dateField }
 * @param {string} templatePath GET -> the blank PDF
 * @param {string} prefillPath  GET -> { fields } pre-filled from the profile
 * @param {string} formLabel    short name used in messages ("TD1", "provincial TD1")
 */
export default function FillableTaxFormPdf({ itemId, metaPath, templatePath, prefillPath, formLabel }) {
  const navigate = useNavigate();
  const accountId = localStorage.getItem("accountId");
  const draftKey = `form_${accountId}_${itemId}`;

  const containerRef = useRef(null);
  const docRef = useRef(null);
  const fieldsRef = useRef({}); // field name -> widget entries from pdf.js getFieldObjects()
  const metaRef = useRef(null);
  const saveTimerRef = useRef(null);
  const pageViewsRef = useRef([]); // pdf.js PDFPageView per page, re-drawn on resize / zoom

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [meta, setMeta] = useState(null);
  const [signature, setSignature] = useState("");
  const [initialSignature, setInitialSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const storage = () => docRef.current?.annotationStorage;

  // Read the current value of every form field from the PDF
  const collectValues = useCallback(() => {
    const result = {};
    const store = storage();
    if (!store) return result;
    for (const [name, widgets] of Object.entries(fieldsRef.current)) {
      const type = widgets[0]?.type;
      if (type === "text") {
        const w = widgets[0];
        const stored = store.getValue(w.id, { value: w.value ?? "" });
        result[name] = String(stored?.value ?? "");
      } else if (type === "checkbox") {
        const w = widgets[0];
        const stored = store.getValue(w.id, { value: w.value && w.value !== "Off" });
        result[name] = Boolean(stored?.value);
      } else if (type === "radiobutton") {
        const selected = widgets.find((w) => store.getValue(w.id, { value: false })?.value === true);
        if (selected) result[name] = String(selected.exportValues ?? "");
      }
    }
    return result;
  }, []);

  // Set a value both in the PDF's form storage and in the rendered input (if already drawn)
  const setFieldValue = useCallback((name, value) => {
    const widgets = fieldsRef.current[name];
    const store = storage();
    if (!widgets || !store) return;
    const type = widgets[0]?.type;
    for (const w of widgets) {
      let v;
      if (type === "text") v = String(value ?? "");
      else if (type === "checkbox") v = Boolean(value);
      else if (type === "radiobutton") v = String(w.exportValues) === String(value);
      else continue;
      store.setValue(w.id, { value: v });
      const el = containerRef.current?.querySelector(`[data-element-id="${w.id}"]`);
      if (el) {
        if (type === "text") el.value = v;
        else el.checked = v;
      }
    }
  }, []);

  // Total line = all other amount lines (the form's own calculation does not run in the app)
  const updateTotal = useCallback(() => {
    const m = metaRef.current;
    if (!m?.autoTotal || !m.totalField) return;
    const values = collectValues();
    const lines = m.amountFields.filter((n) => n !== m.totalField).map((n) => values[n]);
    if (!lines.some((v) => String(v || "").trim() !== "")) return;
    const total = lines.reduce((sum, v) => sum + parseAmount(v), 0);
    setFieldValue(m.totalField, total.toFixed(2));
  }, [collectValues, setFieldValue]);

  const saveDraft = useCallback(
    (extra = {}) => {
      if (!accountId) return;
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ fillablePdf: true, fields: collectValues(), signature, ...extra })
        );
        if (localStorage.getItem(`status_${accountId}_${itemId}`) !== "complete") {
          localStorage.setItem(`status_${accountId}_${itemId}`, "in-progress");
        }
      } catch {
        // ignore storage failures
      }
    },
    [accountId, draftKey, itemId, collectValues, signature]
  );

  // Page width in CSS pixels = the PDF area's inner width (pages have no border, see CSS)
  const pageWidth = () => {
    const el = containerRef.current;
    const inner = el ? el.clientWidth - 8 : 0; // 4px padding each side
    return Math.max(300, inner);
  };

  // Re-draw pages at the new width / pixel density when the window resizes or the browser zooms
  // (zooming fires "resize"), so the form stays sharp instead of a stretched image
  useEffect(() => {
    let timer = null;
    let lastKey = "";
    const redraw = async () => {
      const width = pageWidth();
      const key = `${width}:${window.devicePixelRatio}`;
      if (key === lastKey || pageViewsRef.current.length === 0) return;
      lastKey = key;
      for (const pv of pageViewsRef.current) {
        try {
          const scale = width / pv.pdfPage.getViewport({ scale: 1 }).width;
          pv.update({ scale: scale / PDF_TO_CSS_UNITS });
          if (pv.renderingState !== 0) pv.reset();
          await pv.draw();
        } catch (e) {
          console.warn("[TaxForm] Page re-draw failed:", e?.message);
        }
      }
    };
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(redraw, 250);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load layout + template, apply draft or profile prefill, render pages with fillable fields
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let formMeta;
        try {
          ({ data: formMeta } = await API.get(metaPath));
        } catch (e) {
          throw new Error(e?.response?.data?.message || `Could not load the ${formLabel} form.`);
        }
        if (cancelled) return;
        metaRef.current = formMeta;
        setMeta(formMeta);

        let data;
        try {
          ({ data } = await API.get(templatePath, { responseType: "arraybuffer" }));
        } catch (e) {
          throw new Error(`Could not load the ${formLabel} form.`);
        }
        if (cancelled) return;

        const { pdfjsLib, viewer } = await loadPdfJs();
        const doc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) return;
        docRef.current = doc;

        const fieldObjects = (await doc.getFieldObjects()) || {};
        fieldsRef.current = Object.fromEntries(
          Object.entries(fieldObjects).filter(([, widgets]) =>
            ["text", "checkbox", "radiobutton"].includes(widgets?.[0]?.type)
          )
        );

        // Saved draft wins; otherwise pre-fill from the profile
        let initial = null;
        try {
          const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
          if (draft?.fillablePdf && draft.fields) {
            initial = draft.fields;
            if (draft.signature) {
              setInitialSignature(draft.signature);
              setSignature(draft.signature);
            }
          }
        } catch {
          initial = null;
        }
        if (!initial) {
          try {
            const { data: prefill } = await API.get(prefillPath);
            initial = prefill?.fields || {};
          } catch (e) {
            console.warn("[TaxForm] Prefill unavailable:", e?.message);
            initial = {};
          }
        }
        for (const [name, value] of Object.entries(initial)) setFieldValue(name, value);

        // Render each page at the container's width with interactive form fields
        const container = containerRef.current;
        if (!container || cancelled) return;
        container.innerHTML = "";
        pageViewsRef.current = [];
        const eventBus = new viewer.EventBus();
        const linkService = new viewer.SimpleLinkService();
        const fieldObjectsPromise = Promise.resolve(fieldObjects);
        const width = pageWidth();
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          if (cancelled) return;
          const scale = width / page.getViewport({ scale: 1 }).width;
          const pageContainer = document.createElement("div");
          container.appendChild(pageContainer);
          const pageView = new viewer.PDFPageView({
            container: pageContainer,
            id: i,
            scale: scale / PDF_TO_CSS_UNITS,
            defaultViewport: page.getViewport({ scale }),
            eventBus,
            textLayerMode: 0,
            annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
            layerProperties: () => ({
              annotationStorage: doc.annotationStorage,
              linkService,
              fieldObjectsPromise,
              hasJSActionsPromise: Promise.resolve(false),
              enableScripting: false,
              downloadManager: null,
            }),
          });
          pageView.setPdfPage(page);
          pageViewsRef.current.push(pageView);
          await pageView.draw();
        }
        updateTotal();
      } catch (err) {
        console.error("[TaxForm] Failed to load fillable PDF", err);
        if (!cancelled) setLoadError(err?.message || `Could not load the ${formLabel} form.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(saveTimerRef.current);
      pageViewsRef.current = [];
      docRef.current?.destroy?.();
      docRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the drawn signature in the saved draft
  useEffect(() => {
    if (!loading && !loadError && signature) saveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Typing in the PDF: keep the total up to date and auto-save the draft
  const handleFormInput = () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateTotal();
      saveDraft();
    }, 500);
  };

  const handleSaveLater = async () => {
    updateTotal();
    saveDraft();
    try {
      await API.post(`/api/registration/${accountId}/items/${itemId}/complete`, {
        status: "in-progress",
        progress: 50,
        formData: { fillablePdf: true, draft: true },
      });
    } catch (e) {
      console.warn("[TaxForm] Could not sync draft status:", e?.message);
    }
    showSuccess("Form saved! You can come back to finish it later.");
  };

  const handleSubmit = async () => {
    if (!accountId) return showError("Account ID not found. Please log in again.");
    if (!signature) return showError("Please sign the form before submitting.");
    updateTotal();
    const fields = collectValues();
    setSubmitting(true);
    try {
      await API.post(`/api/registration/${accountId}/items/${itemId}/complete`, {
        status: "complete",
        progress: 100,
        formData: {
          fillablePdf: true,
          year: meta?.year || "",
          fields,
          signature,
          signedDate: (meta?.dateField && fields[meta.dateField]) || "",
        },
      });
      saveDraft();
      localStorage.setItem(`status_${accountId}_${itemId}`, "complete");
      showSuccess(`${meta?.title || formLabel} submitted successfully!`);
      setTimeout(() => navigate("/register"), 1200);
    } catch (err) {
      console.error("[TaxForm] Submit failed", err);
      showError(err?.response?.data?.message || err?.response?.data?.error || `Failed to submit the ${formLabel}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-3">
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <div className="flex flex-col items-center mb-4">
          <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 56 }} />
          <h1 className="text-xl font-semibold mt-3 text-center">{meta?.title || formLabel}</h1>
          <p className="text-sm text-gray-600 mt-1 text-center">
            Fill in the form below, then sign at the bottom of the page and submit.
          </p>
        </div>

        {loading && <div className="text-center text-gray-600 py-10">Loading form…</div>}
        {loadError && (
          <div className="text-center py-10">
            <div className="text-red-600">{loadError}</div>
            <button type="button" className="mt-4 px-4 py-2 border rounded" onClick={() => navigate("/register")}>
              Back to registration
            </button>
          </div>
        )}

        <div
          ref={containerRef}
          className="pdfViewer td1-pdf"
          onInput={handleFormInput}
          onChange={handleFormInput}
          style={{
            background: "#e5e7eb",
            padding: 4,
            display: loadError ? "none" : "block",
            // visibility (not display:none) keeps the width measurable while pages render
            visibility: loading ? "hidden" : "visible",
          }}
        />

        {!loading && !loadError && (
          <div className="bg-white rounded border border-gray-200 p-4 mt-4">
            <h2 className="font-semibold mb-1">Signature</h2>
            <p className="text-sm text-gray-600 mb-3">
              I certify that the information given on this form is correct and complete. Your signature will be
              placed on the signature line of the form.
            </p>
            <SignaturePanel onSignatureChange={setSignature} initialSignature={initialSignature} required />
            <div className="flex flex-wrap justify-end gap-3 mt-4">
              <button type="button" className="px-4 py-2 border rounded" onClick={() => navigate("/register")}>
                Back
              </button>
              <button type="button" className="px-4 py-2 border rounded" onClick={handleSaveLater} disabled={submitting}>
                Save for later
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-60"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
